import React, { useContext, useEffect, useState } from "react";
import { Inbox, Paperclip, Send, Sparkles } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { API_BASE_URL } from "../api";
import { Button, Field, SectionHeader, StatusPill, SurfaceCard, TextareaField } from "../components/designSystem";

export default function Messaging() {
  const { token, user } = useContext(AuthContext);
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;

    async function loadMessages() {
      setLoading(true);
      setError("");

      try {
        const [inboxResponse, sentResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/message/inbox`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/message/sent`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const [inboxData, sentData] = await Promise.all([inboxResponse.json(), sentResponse.json()]);

        if (!inboxResponse.ok || !sentResponse.ok) {
          throw new Error(inboxData.error || sentData.error || "Failed to load messages.");
        }

        if (active) {
          setInbox(Array.isArray(inboxData) ? inboxData : []);
          setSent(Array.isArray(sentData) ? sentData : []);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message || "Failed to load messages.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    if (token) {
      loadMessages();
    }

    return () => {
      active = false;
    };
  }, [token]);

  async function searchRecipient() {
    if (!recipientQuery.trim()) {
      setRecipient(null);
      return;
    }

    setSearching(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/find-user/single?email=${encodeURIComponent(recipientQuery.trim())}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to find recipient.");
      }

      if (!data.user) {
        setRecipient(null);
        setMessage("No user found for that email.");
        return;
      }

      setRecipient(data.user);
      setMessage(`Recipient found: ${data.user.username || data.user.email}`);
    } catch (searchError) {
      setRecipient(null);
      setError(searchError.message || "Unable to find recipient.");
    } finally {
      setSearching(false);
    }
  }

  async function sendMessage(event) {
    event.preventDefault();
    if (!recipient?._id || !content.trim()) {
      setError("Choose a valid recipient and enter a message.");
      return;
    }

    setSending(true);
    setError("");
    setMessage("");

    try {
      let response;

      if (attachments.length) {
        const formData = new FormData();
        formData.append("recipient", recipient._id);
        formData.append("content", content.trim());
        attachments.forEach((file) => formData.append("attachments", file));

        response = await fetch(`${API_BASE_URL}/api/message/direct/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } else {
        response = await fetch(`${API_BASE_URL}/api/message/direct`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ recipient: recipient._id, content: content.trim() }),
        });
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message.");
      }

      setSent((current) => [data, ...current]);
      setContent("");
      setAttachments([]);
      setMessage("Message sent.");
    } catch (sendError) {
      setError(sendError.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  }

  async function markAsRead(messageId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/message/${messageId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update message.");
      }

      setInbox((current) => current.map((item) => (item._id === messageId ? data : item)));
    } catch (readError) {
      setError(readError.message || "Failed to update message.");
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(244,114,182,0.14),_transparent_24%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(244,114,182,0.14),_transparent_24%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Direct Messaging"
          title="Communicate inside the trading workspace"
          description="Messages should feel like part of the operating system: fast, attached to the account, and useful for team or mentor communication."
          action={<StatusPill tone="brand"><Sparkles className="mr-2 h-3.5 w-3.5" />Premium module</StatusPill>}
        />
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      {message ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{message}</p>
        </SurfaceCard>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard>
          <SectionHeader
            eyebrow="Compose"
            title="Send a direct message"
            description="Find a user by email, write the message, and optionally attach supporting files or screenshots."
          />
          <form onSubmit={sendMessage} className="mt-6 space-y-5">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <Field
                label="Recipient Email"
                placeholder="teammate@desk.com"
                value={recipientQuery}
                onChange={(event) => setRecipientQuery(event.target.value)}
              />
              <Button type="button" variant="secondary" onClick={searchRecipient} disabled={searching}>
                {searching ? "Searching..." : "Find user"}
              </Button>
            </div>

            {recipient ? (
              <div className="rounded-3xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                Sending as {user?.username || user?.email} to {recipient.username || recipient.email}.
              </div>
            ) : null}

            <TextareaField
              label="Message"
              rows={8}
              placeholder="Send review feedback, share trade context, or discuss session plans."
              value={content}
              onChange={(event) => setContent(event.target.value)}
            />

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">Attachments</span>
              <input
                type="file"
                multiple
                onChange={(event) => setAttachments(Array.from(event.target.files || []))}
                className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100"
              />
            </label>
            {attachments.length ? (
              <div className="flex flex-wrap gap-2">
                {attachments.map((file) => (
                  <StatusPill key={`${file.name}-${file.size}`} tone="neutral">
                    <Paperclip className="mr-2 h-3.5 w-3.5" />
                    {file.name}
                  </StatusPill>
                ))}
              </div>
            ) : null}

            <Button type="submit" variant="primary" disabled={sending}>
              <Send className="h-4 w-4" />
              {sending ? "Sending..." : "Send message"}
            </Button>
          </form>
        </SurfaceCard>

        <div className="space-y-6">
          <SurfaceCard>
            <SectionHeader eyebrow="Inbox" title="Received messages" description="Unread items stay visible so the workspace becomes a real collaboration surface instead of a hidden utility."
            />
            <div className="mt-6 space-y-4">
              {loading ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading inbox...</p>
              ) : inbox.length ? (
                inbox.map((item) => (
                  <div key={item._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">From {item.sender || "Unknown sender"}</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{new Date(item.createdAt).toLocaleString()}</p>
                      </div>
                      <StatusPill tone={item.read ? "neutral" : "warning"}>{item.read ? "Read" : "Unread"}</StatusPill>
                    </div>
                    <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-600 dark:text-slate-300">{item.content}</p>
                    {Array.isArray(item.attachments) && item.attachments.length ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {item.attachments.map((attachment) => (
                          <a key={attachment.url} href={attachment.url} target="_blank" rel="noreferrer">
                            <StatusPill tone="brand">{attachment.name || attachment.type}</StatusPill>
                          </a>
                        ))}
                      </div>
                    ) : null}
                    {!item.read ? (
                      <div className="mt-5">
                        <Button type="button" variant="secondary" onClick={() => markAsRead(item._id)}>
                          Mark as read
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                  No incoming messages yet.
                </div>
              )}
            </div>
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader eyebrow="Sent" title="Outbound history" description="Keep message history visible so support, team coordination, and trade reviews remain traceable." />
            <div className="mt-6 space-y-4">
              {loading ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading sent messages...</p>
              ) : sent.length ? (
                sent.map((item) => (
                  <div key={item._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">To {item.recipient || "Recipient"}</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{new Date(item.createdAt).toLocaleString()}</p>
                      </div>
                      <Inbox className="h-5 w-5 text-slate-400" />
                    </div>
                    <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-600 dark:text-slate-300">{item.content}</p>
                    {Array.isArray(item.attachments) && item.attachments.length ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {item.attachments.map((attachment) => (
                          <a key={attachment.url} href={attachment.url} target="_blank" rel="noreferrer">
                            <StatusPill tone="neutral">{attachment.name || attachment.type}</StatusPill>
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                  No sent messages yet.
                </div>
              )}
            </div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}